import { Directive, Input, Output, EventEmitter } from "@angular/core";

import { coerceBooleanProperty, BooleanInput } from "@vattenfall/util";

import { InputsElementAccessor } from "./inputs-accessor";

/**
 * The `OptionsElementAccessor` contains methods and properties shared by all input components that selects single options.
 * It require a generic type to be specified, this type should correspond to the value emitted on input change.
 */
@Directive()
export class OptionsElementAccessor extends InputsElementAccessor<boolean, HTMLInputElement> {
  /**
   * The initial value of the control.
   *
   * A string value or an array of string values in case the input is a multi-select option list.
   */
  @Input()
  get value(): string | null {
    return this._value;
  }
  set value(value: string | null) {
    this._value = value;
    this.setProperty("value", this._value);
    if (this.ngControl) this.ngControl.control?.setValue(value);
  }
  private _value: string | null = null;
  /**
   * Whether the checkbox or radio is checked.
   *
   * Default is `false`.
   */
  @Input()
  get checked(): boolean {
    return this._checked;
  }
  set checked(value: BooleanInput) {
    this._checked = coerceBooleanProperty(value);
    this.setProperty("checked", this._checked);
  }
  private _checked = false;
  @Output() readonly checkedChange = new EventEmitter<boolean>();
  /**
   * Whether only one checkbox can be selected at the same time.
   *
   * Default is `false`.
   */
  @Input()
  get radio(): boolean {
    return this._radio;
  }
  set radio(value: BooleanInput) {
    this._radio = coerceBooleanProperty(value);
  }
  private _radio = false;
  /**
   * Whether the checkbox or radio is indeterminate. Indeterminate checkboxes are displayed in a state which is distinct both from being checked or being unchecked.
   *
   * Default is `false`.
   */
  @Input()
  get indeterminate(): boolean {
    return this._indeterminate;
  }
  set indeterminate(value: BooleanInput) {
    this._indeterminate = coerceBooleanProperty(value);
    this.setProperty("indeterminate", this._indeterminate);
  }
  private _indeterminate = false;

  onCheckboxChange(checked: boolean) {
    this.onChange(checked);
    this.checkedChange.emit(checked);
  }
}
